import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, Stack, Alert } from '@mui/material'
import { CommonSlider } from '@/components/common'
import { LoginDialog } from '@/components/auth'
import {
  ChipSelector,
  AppointmentConfirmedDialog,
  ConfirmAppointmentButton,
  IneligibilityDialog,
  ResultCard,
  DonorPortalScreenMessage,
  SuggestionsSection,
  RescheduleConfirmationDialog,
} from '@/components/donation'
import {
  useGetAppointmentAvailability,
  useConfirmAppointment,
  useGetDriveSessionSingle,
  useRescheduleAppointment,
} from '@/hooks'
import {
  timeToMinutes,
  formatUtcDate,
  formatDisplayTime,
  formatIsoDateWithWeekday,
  parseScheduledDonationsParams,
  getGuestConfirmAppointmentParams,
  getScheduledDonationsParams,
  buildResultItem,
  trackDailyStoryConversion,
} from '@/utils'
import { useDonorContext } from '@/context'
import { ScheduleDonationSkeleton } from './Skeleton'
import {
  ROUTES,
  STORAGE_KEYS,
  DONOR_PORTAL_SCREEN_CONFIG_CODE,
} from '@/constants'
import { localStorageService, sessionStorageService } from '@/services'

const SUCCESS_STATUS_CODE = 201
const INELIGIBLE_STATUS_CODE = 400
const INELIGIBLE_RESPONSE = 'INELIGIBLE'
const DEFAULT_BOOKING_ERROR =
  'This appointment could not be booked. Please choose another time.'

export const ScheduleDonation = () => {
  const { donor } = useDonorContext()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const {
    appointmentAvailability,
    getAppointmentAvailability,
    clearAppointmentAvailability,
    loadingAppointmentAvailability,
  } = useGetAppointmentAvailability()
  const { confirmAppointment, loadingConfirmAppointment } =
    useConfirmAppointment()
  const { rescheduleAppointment, loadingRescheduleAppointment } =
    useRescheduleAppointment()
  const {
    driveSessionSingle,
    getDriveSessionSingle,
    loadingDriveSessionSingle,
  } = useGetDriveSessionSingle()

  const [timeRange, setTimeRange] = useState(['8:00 AM', '10:00 AM'])
  const [formData, setFormData] = useState({})
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [confirmedSlot, setConfirmedSlot] = useState(null)
  const [openLoginDialog, setOpenLoginDialog] = useState(false)
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false)
  const [pendingAppointmentConfirmation, setPendingAppointmentConfirmation] =
    useState(false)
  const [ineligibilityDialogOpen, setIneligibilityDialogOpen] = useState(false)
  const [errorData, setErrorData] = useState(null)
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false)
  const [pendingSlotId, setPendingSlotId] = useState(null)
  const [pendingRescheduleInfo, setPendingRescheduleInfo] = useState(null)
  const [bookingError, setBookingError] = useState(null)
  useEffect(() => {
    const parsedFormData = parseScheduledDonationsParams(searchParams)
    setFormData(parsedFormData)
    setSelectedSlot(null)
    setBookingError(null)
    clearAppointmentAvailability()

    if (parsedFormData.operation_id && parsedFormData.operation_type) {
      const driveSessionParams = {
        operation_id: parsedFormData.operation_id,
        operation_type: parsedFormData.operation_type,
      }
      if (parsedFormData.procedure_type_id) {
        driveSessionParams.procedure_type_id = parsedFormData.procedure_type_id
      }
      getDriveSessionSingle(driveSessionParams)
    }
  }, [searchParams, clearAppointmentAvailability, getDriveSessionSingle])

  useEffect(() => {
    if (loadingDriveSessionSingle || !driveSessionSingle) return

    const bookingClosedForDrive =
      formData.operation_type === 'drives' &&
      driveSessionSingle.online_booking_allowed === false

    if (driveSessionSingle.is_cancelled || bookingClosedForDrive) {
      clearAppointmentAvailability()
      return
    }

    const availabilityParams = { ...formData }
    if (!availabilityParams.procedure_type_id) {
      delete availabilityParams.procedure_type_id
    }
    getAppointmentAvailability(availabilityParams)
  }, [
    driveSessionSingle,
    loadingDriveSessionSingle,
    formData.operation_id,
    formData.operation_type,
    formData.procedure_type_id,
    clearAppointmentAvailability,
    getAppointmentAvailability,
  ])
  useEffect(() => {
    if (pendingAppointmentConfirmation && donor?.id && !openLoginDialog) {
      setPendingAppointmentConfirmation(false)
      handleConfirmAppointment()
    }
  }, [donor?.id, pendingAppointmentConfirmation, openLoginDialog])

  useEffect(() => {
    if (!appointmentAvailability || appointmentAvailability.length === 0) return

    const availableEntries = appointmentAvailability.filter(entry =>
      Array.isArray(entry?.procedure_types)
        ? entry.procedure_types.some(pt =>
            Array.isArray(pt?.slots)
              ? pt.slots.some(slot => !!slot?.is_available)
              : false
          )
        : false
    )

    if (!availableEntries.length) return

    // Find earliest and latest times based on time of day (not full timestamp)
    const earliestAvail = availableEntries.reduce((min, a) => {
      const minTimeStr = formatUtcDate(min.start_time, 'h:mm a')
      const aTimeStr = formatUtcDate(a.start_time, 'h:mm a')
      const minMinutes = timeToMinutes(minTimeStr)
      const aMinutes = timeToMinutes(aTimeStr)
      return aMinutes < minMinutes ? a : min
    })
    const latestAvail = availableEntries.reduce((max, a) => {
      const maxTimeStr = formatUtcDate(max.start_time, 'h:mm a')
      const aTimeStr = formatUtcDate(a.start_time, 'h:mm a')
      const maxMinutes = timeToMinutes(maxTimeStr)
      const aMinutes = timeToMinutes(aTimeStr)
      return aMinutes > maxMinutes ? a : max
    })

    const startLabel = formatUtcDate(earliestAvail.start_time, 'hh:mm a')
    const endLabel = formatUtcDate(latestAvail.start_time, 'hh:mm a')
    setTimeRange([startLabel, endLabel])
  }, [appointmentAvailability])

  const donorName = useMemo(() => {
    return donor?.first_name + ' ' + donor?.last_name || ''
  }, [donor])

  const redirectUrl = useMemo(() => {
    return window.location.origin + location.pathname + location.search
  }, [location])

  const driveSessionItem = useMemo(() => {
    if (!driveSessionSingle) return null
    const { latitude, longitude } = formData
    return buildResultItem(driveSessionSingle, { latitude, longitude })
  }, [driveSessionSingle, formData])

  const isOperationCancelled = !!driveSessionSingle?.is_cancelled
  const isOnlineBookingClosed =
    formData.operation_type === 'drives' &&
    driveSessionSingle != null &&
    driveSessionSingle.online_booking_allowed === false
  const isBookingUnavailable = isOperationCancelled || isOnlineBookingClosed
  const canShowSlotPicker =
    !!driveSessionSingle && !loadingDriveSessionSingle && !isBookingUnavailable
  const cancelledTitle =
    formData.operation_type === 'sessions'
      ? 'This session has been cancelled'
      : 'This drive has been cancelled'
  const onlineBookingClosedTitle =
    formData.operation_type === 'sessions'
      ? 'Online booking is no longer available for this session'
      : 'Online booking is no longer available for this drive'

  const builtSuggestions = useMemo(() => {
    const suggestions = driveSessionSingle?.suggestions ?? []
    if (!suggestions.length) return []
    const { latitude, longitude } = formData
    return suggestions.map(row => buildResultItem(row, { latitude, longitude }))
  }, [driveSessionSingle?.suggestions, formData])

  const handleSelectSuggestion = suggestion => {
    navigate(
      `${ROUTES.SCHEDULE_DONATION}?${getScheduledDonationsParams({
        ...formData,
        operation_id: suggestion.operation_id,
        operation_type: suggestion.operation_type,
        date: suggestion.date || formData.date,
        procedure_type_id: formData.procedure_type_id,
      }).toString()}`
    )
  }

  const availableAppointments = useMemo(() => {
    if (!appointmentAvailability || appointmentAvailability.length === 0)
      return []

    const [rangeStart, rangeEnd] = timeRange
    const rangeStartMinutes = timeToMinutes(rangeStart)
    const rangeEndMinutes = timeToMinutes(rangeEnd)

    return appointmentAvailability
      .map((appt, index) => ({
        startTime: appt.start_time,
        endTime: appt.end_time,
        time: formatUtcDate(appt.start_time, 'h:mm a'),
        procedureTypes: appt?.procedure_types.map(pt => ({
          id: `appt-chip-${pt.procedure_type_id}-${index}`,
          availableSlots: pt.available_slots,
          totalSlots: pt.total_slots,
          occupiedSlots: pt.occupied_slots,
          disabled: pt.available_slots === 0,
          text1: pt.procedure_name,
          text2:
            pt.available_slots === 0
              ? 'Full'
              : `${pt.available_slots} Opening${pt.available_slots !== 1 ? 's' : ''}`,
          ...pt,
        })),
      }))
      .filter(appt => {
        const slotStartTimeMinutes = timeToMinutes(
          formatUtcDate(appt.startTime, 'h:mm a')
        )

        // Handle time ranges that cross midnight
        if (rangeEndMinutes < rangeStartMinutes) {
          // Range crosses midnight (e.g., 4:00 PM to 2:45 AM)
          // Slot is valid if it's >= start time OR <= end time
          return (
            slotStartTimeMinutes >= rangeStartMinutes ||
            slotStartTimeMinutes <= rangeEndMinutes
          )
        } else {
          // Normal range within the same day
          return (
            slotStartTimeMinutes >= rangeStartMinutes &&
            slotStartTimeMinutes <= rangeEndMinutes
          )
        }
      })
      .sort((a, b) => {
        // Sort by time (lowest to highest)
        // Convert time strings to minutes for comparison
        const timeA = timeToMinutes(formatUtcDate(a.startTime, 'h:mm a'))
        const timeB = timeToMinutes(formatUtcDate(b.startTime, 'h:mm a'))

        // If range crosses midnight, prioritize times that are after midnight (<= rangeEndMinutes)
        if (rangeEndMinutes < rangeStartMinutes) {
          const aIsAfterMidnight = timeA <= rangeEndMinutes
          const bIsAfterMidnight = timeB <= rangeEndMinutes

          // If one is after midnight and the other isn't, the after-midnight one comes first
          if (aIsAfterMidnight && !bIsAfterMidnight) return -1
          if (!aIsAfterMidnight && bIsAfterMidnight) return 1

          // If both are after midnight or both are before midnight, sort by time
          return timeA - timeB
        }
        return timeA - timeB
      })
  }, [appointmentAvailability, timeRange])

  const handleAppointmentSelect = slot => {
    setSelectedSlot(slot)
  }

  const handleConfirmAppointment = async () => {
    setErrorData(null)
    setBookingError(null)
    let slotId = null
    let confirmedSlotData = null
    const isGuest =
      localStorageService.getItem(STORAGE_KEYS.IS_GUEST) === 'true'

    if (selectedSlot && selectedSlot.available_slots > 0) {
      confirmedSlotData = selectedSlot?.slots.find(slot => slot.is_available)
      slotId = confirmedSlotData?.slot_id
      setConfirmedSlot(confirmedSlotData)
    }

    if (!slotId) return

    if (donor?.id) {
      const rescheduleData = sessionStorageService.getItem(
        STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID,
        null,
        true
      )

      if (rescheduleData?.id) {
        setPendingSlotId(slotId)
        setPendingRescheduleInfo({
          existing: {
            date: rescheduleData.date
              ? formatIsoDateWithWeekday(rescheduleData.date)
              : '',
            time: rescheduleData.start_time
              ? formatDisplayTime(rescheduleData.start_time, 'h:mm a')
              : '',
            location: rescheduleData.location || '',
            procedure_type: rescheduleData.procedure_type || '',
            address: rescheduleData.address || '',
          },
          new: {
            date: driveSessionSingle?.date
              ? formatIsoDateWithWeekday(driveSessionSingle.date)
              : formData.date
                ? formatIsoDateWithWeekday(formData.date)
                : '',
            time: confirmedSlotData?.start_time
              ? formatDisplayTime(confirmedSlotData.start_time, 'h:mm a')
              : '',
            location: driveSessionItem?.name || driveSessionSingle?.name || '',
            address: driveSessionItem?.address || '',
          },
        })
        setRescheduleDialogOpen(true)
        return
      }

      await doConfirmAppointment(slotId)
    } else if (isGuest) {
      const params = getGuestConfirmAppointmentParams({
        date: formData.date,
        slot_id: slotId,
        start_time: confirmedSlotData?.start_time,
        operation_id: formData.operation_id,
        operation_type: formData.operation_type,
      })
      navigate(`${ROUTES.GUEST_CONFIRM_APPOINTMENT}?${params.toString()}`)
    } else {
      setOpenLoginDialog(true)
    }
  }

  const doConfirmAppointment = async slotId => {
    const response = await confirmAppointment({
      donor_id: donor.id,
      slot_id: slotId,
      operation_id: formData.operation_id,
      operation_type: formData.operation_type,
    })
    if (response?.status_code === SUCCESS_STATUS_CODE && response?.data) {
      setBookingError(null)
      trackDailyStoryConversion()
      setOpenSuccessDialog(true)
      resetForm()
    } else if (
      response?.status_code === INELIGIBLE_STATUS_CODE &&
      response?.response === INELIGIBLE_RESPONSE
    ) {
      setErrorData(response?.data)
      setIneligibilityDialogOpen(true)
    } else if (response?.status_code >= 400) {
      setBookingError(response?.response || DEFAULT_BOOKING_ERROR)
      resetForm()
    }
  }

  const handleProceedReschedule = async () => {
    setBookingError(null)
    const rescheduleData = sessionStorageService.getItem(
      STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID,
      null,
      true
    )
    const response = await rescheduleAppointment({
      donor_id: String(donor.id),
      new_slot_id: String(pendingSlotId),
      old_appointment_id: String(rescheduleData?.id),
      operation_id: formData.operation_id,
      operation_type: formData.operation_type,
    })
    if (response?.status_code === SUCCESS_STATUS_CODE && response?.data) {
      sessionStorageService.removeItem(STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID)
      setRescheduleDialogOpen(false)
      setPendingSlotId(null)
      setPendingRescheduleInfo(null)
      setBookingError(null)
      trackDailyStoryConversion()
      setOpenSuccessDialog(true)
      resetForm()
    } else if (
      response?.status_code === INELIGIBLE_STATUS_CODE &&
      response?.response === INELIGIBLE_RESPONSE
    ) {
      setRescheduleDialogOpen(false)
      setPendingSlotId(null)
      setPendingRescheduleInfo(null)
      setErrorData(response?.data)
      setIneligibilityDialogOpen(true)
    } else if (response?.status_code >= 400) {
      setRescheduleDialogOpen(false)
      setPendingSlotId(null)
      setPendingRescheduleInfo(null)
      setBookingError(response?.response || DEFAULT_BOOKING_ERROR)
      resetForm()
    }
  }

  const handleBookAsNew = async () => {
    sessionStorageService.removeItem(STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID)
    setRescheduleDialogOpen(false)
    const slotId = pendingSlotId
    setPendingSlotId(null)
    setPendingRescheduleInfo(null)
    await doConfirmAppointment(slotId)
  }

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false)
    setPendingSlotId(null)
    setPendingRescheduleInfo(null)
  }

  const resetForm = () => {
    setSelectedSlot(null)
    setErrorData(null)
    if (!formData.operation_id || !formData.operation_type) return
    const driveSessionParams = {
      operation_id: formData.operation_id,
      operation_type: formData.operation_type,
    }
    if (formData.procedure_type_id) {
      driveSessionParams.procedure_type_id = formData.procedure_type_id
    }
    getDriveSessionSingle(driveSessionParams)
  }

  const handleSuccessLogin = () => {
    setOpenLoginDialog(false)
    const isGuest =
      localStorageService.getItem(STORAGE_KEYS.IS_GUEST) === 'true'

    if (isGuest) handleConfirmAppointment()
    else setPendingAppointmentConfirmation(true)
  }

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false)
    if (window.history.length > 1) navigate(-1)
    else navigate(ROUTES.HOME)
  }

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2 }, mb: { xs: 2, md: 2 } }}>
        <DonorPortalScreenMessage
          code={DONOR_PORTAL_SCREEN_CONFIG_CODE.APPOINTMENT_SCHEDULING}
        />
      </Box>
      <Box sx={{ px: { xs: 2, md: 2 }, mb: { xs: 2, md: 3 } }}>
        <ResultCard
          item={driveSessionItem || {}}
          isClickable={false}
          loading={loadingDriveSessionSingle}
          date={
            driveSessionSingle?.date
              ? formatIsoDateWithWeekday(driveSessionSingle.date)
              : formData.date
                ? formatIsoDateWithWeekday(formData.date)
                : undefined
          }
        />
      </Box>

      {(isBookingUnavailable || loadingDriveSessionSingle) && (
        <Box
          sx={{
            px: { xs: 2, md: 2 },
            mt: { xs: 3, md: 4 },
            mb: { xs: 2, md: 3 },
          }}
        >
          <SuggestionsSection
            loading={loadingDriveSessionSingle}
            hasResults={false}
            hasSuggestions={builtSuggestions.length > 0}
            emptyTitle={
              loadingDriveSessionSingle
                ? undefined
                : isOperationCancelled
                  ? cancelledTitle
                  : onlineBookingClosedTitle
            }
            emptySubtitle={
              loadingDriveSessionSingle
                ? undefined
                : isOperationCancelled
                  ? 'This operation is no longer available for booking.'
                  : 'The online booking period for this drive has ended. Here are other drives you may want to book.'
            }
            suggestionsTitle='You might be interested in'
            suggestionsSubtitle='Here are some other options that might work for you'
          >
            <Stack spacing={2}>
              {builtSuggestions.map((resultItem, index) => (
                <ResultCard
                  key={`cancelled-suggestion-${resultItem.operation_id}-${resultItem.date}-${index}`}
                  item={resultItem}
                  operationDate={
                    resultItem.date
                      ? formatIsoDateWithWeekday(resultItem.date)
                      : undefined
                  }
                  onSelect={() => handleSelectSuggestion(resultItem)}
                />
              ))}
            </Stack>
          </SuggestionsSection>
        </Box>
      )}

      {canShowSlotPicker && (
        <>
          <Box sx={{ px: { xs: 2, md: 2 }, mb: { xs: 1, md: 0 } }}>
            <CommonSlider
              id='appointment-time-range'
              variant='time'
              isRange={true}
              value={timeRange}
              onChange={setTimeRange}
              step={15}
            />
          </Box>

          {!availableAppointments.length && (
            <Typography
              gutterBottom
              sx={{
                marginTop: { xs: 3, md: 5 },
                textAlign: 'center',
                typography: { xs: 'body2', md: 'h6' },
              }}
            >
              No appointments available
            </Typography>
          )}

          {loadingAppointmentAvailability ? (
            <ScheduleDonationSkeleton />
          ) : (
            <Box
              sx={{
                mt: { xs: 2, md: 4 },
                px: { xs: 1, md: 2 },
                overflowY: { xs: 'hidden', md: 'auto' },
                maxHeight: { xs: '100%', md: '500px' },
              }}
            >
              {availableAppointments.map((appt, index) => {
                const chips = appt.procedureTypes
                return (
                  <Box
                    key={`${appt.startTime}-${index}`}
                    sx={{ mb: { xs: 1.5, md: 2 } }}
                  >
                    <ChipSelector
                      title={appt.time}
                      id={`appointment-appt-${index}`}
                      chips={chips}
                      returnObject={true}
                      value={selectedSlot}
                      onChange={chip => handleAppointmentSelect(chip)}
                    />
                  </Box>
                )
              })}
            </Box>
          )}

          {bookingError && (
            <Alert severity='error' sx={{ mx: 2, mb: 2 }}>
              {bookingError}
            </Alert>
          )}

          <ConfirmAppointmentButton
            selectedSlot={selectedSlot}
            loading={loadingConfirmAppointment || loadingRescheduleAppointment}
            onClick={handleConfirmAppointment}
            hasAppointments={!!availableAppointments.length}
          />
        </>
      )}

      <AppointmentConfirmedDialog
        open={openSuccessDialog}
        name={donorName}
        date={formatIsoDateWithWeekday(formData.date)}
        time={
          confirmedSlot?.start_time
            ? formatDisplayTime(confirmedSlot?.start_time, 'h:mm a')
            : ''
        }
        onClose={handleCloseSuccessDialog}
      />

      <LoginDialog
        open={openLoginDialog}
        redirectUrl={redirectUrl}
        onSuccess={handleSuccessLogin}
        onClose={() => setOpenLoginDialog(false)}
      />

      <IneligibilityDialog
        open={ineligibilityDialogOpen}
        onClose={() => setIneligibilityDialogOpen(false)}
        procedure={errorData?.procedure}
        nextEligibilityDate={errorData?.next_eligibility_date}
      />

      <RescheduleConfirmationDialog
        open={rescheduleDialogOpen}
        onClose={handleCloseRescheduleDialog}
        onReschedule={handleProceedReschedule}
        onBookAsNew={handleBookAsNew}
        loading={loadingRescheduleAppointment || loadingConfirmAppointment}
        existingAppointment={pendingRescheduleInfo?.existing}
        newAppointment={pendingRescheduleInfo?.new}
      />
    </>
  )
}
